/**
 * 「収録動画から」の全幅カード (docs/features/video-import-scenario-key-points-plan.md §5.2)。
 * 動画エディタの「この動画からシナリオを作る」から来たときだけ出る。台本取り込みのカード (ScenarioScriptImportCard) と同じ体裁。
 *
 * - AI が整理した要点を重要度順に並べ、チェックで絞り込む。既定は想定尺に応じた上位 N 件 (利用者が触るまで想定尺に追従)
 * - チェックを変えるたびに事前確認 API を呼び、文字数・省略・エラーを見せる
 * - 要点の不一致 (409): stale は読み直して既定に戻す。outdated は作り直してから読み直す (GET だけでは直らない)
 * - 「元の要点を見る」で話題ごとの元の要点 (keyPoints) を折りたたみで見せる (読むだけ)
 * - × で外すと通常の生成に戻る (選んだ案件名の入力はそのまま残す)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { isAxiosError } from 'axios';
import {
  describeOmitted,
  ensureDigest,
  formatTopicTime,
  getDigest,
  previewSourceVideoBrief,
  suggestedPointIds,
  type DigestStatusResponse,
  type SourceVideoBriefPreview,
} from '../../../../features/videoEditor/sourceVideo';
import { useToast } from '../../../../hooks/useToast';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';
import type { SourceVideoState } from '../sourceVideo';
import genStyles from '../ScenarioGenerator.module.css';
import cardStyles from './ScenarioScriptImport.module.css';
import styles from './ScenarioSourceVideoCard.module.css';

export function ScenarioSourceVideoCard() {
  const { sourceVideo, setSourceVideo } = useScenarioSetup();
  const toast = useToast();
  const [preview, setPreview] = useState<SourceVideoBriefPreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [reloading, setReloading] = useState(false);
  const requestSeq = useRef(0);

  const points = useMemo(() => (sourceVideo ? [...sourceVideo.digest.points].sort((a, b) => a.rank - b.rank) : []), [sourceVideo]);
  const topicsById = useMemo(() => new Map((sourceVideo?.topics ?? []).map((t) => [t.id, t])), [sourceVideo]);
  const selected = useMemo(() => new Set(sourceVideo?.pointIds ?? []), [sourceVideo]);
  const projectId = sourceVideo?.projectId ?? null;
  const digestId = sourceVideo?.digest.id ?? null;
  const durationSec = sourceVideo?.durationSec ?? 30;
  const pointIdsKey = (sourceVideo?.pointIds ?? []).join(',');

  // 読み直し (stale: GET / outdated: 作り直してから GET)。選択は既定に戻す
  const reload = async (rebuild: boolean) => {
    if (!projectId) return;
    setReloading(true);
    try {
      const s: DigestStatusResponse = rebuild ? await ensureDigest(projectId) : await getDigest(projectId);
      if (s.status !== 'ready' || !s.digest) {
        toast.error('要点を読み直せませんでした。動画エディタからやり直してください');
        return;
      }
      const digest = s.digest;
      setSourceVideo((prev) => prev && ({ ...prev, digest, topics: s.topics, totalSegments: s.topics.length, pointIds: suggestedPointIds(digest, prev.durationSec), userTouched: false }));
      toast.success(rebuild ? '収録動画が更新されていたため、要点を作り直しました' : '要点を読み直しました');
    } catch (e) {
      console.error('[ScenarioSourceVideoCard] reload failed:', e);
      toast.error('要点を読み直せませんでした');
    } finally {
      setReloading(false);
    }
  };

  // 事前確認 (保存しない)。選択を変えるたびに呼び、古い応答は捨てる
  useEffect(() => {
    if (!projectId || !digestId) return;
    const pointIds = pointIdsKey ? pointIdsKey.split(',') : [];
    if (pointIds.length === 0) {
      setPreview({ ok: false, error: '要点を1件以上選んでください', error_code: 'source_video_points_required' });
      return;
    }
    const seq = ++requestSeq.current;
    setChecking(true);
    const timer = window.setTimeout(() => {
      previewSourceVideoBrief(projectId, { digest_id: digestId, point_ids: pointIds, duration_sec: durationSec })
        .then((res) => { if (seq === requestSeq.current) setPreview(res); })
        .catch((e: unknown) => {
          if (seq !== requestSeq.current) return;
          const code = isAxiosError(e) ? (e.response?.data as { error_code?: string } | undefined)?.error_code : undefined;
          if (code === 'source_video_digest_stale') { void reload(false); return; }
          if (code === 'source_video_digest_outdated') { void reload(true); return; }
          setPreview({ ok: false, error: '収録動画の内容を確認できませんでした' });
        })
        .finally(() => { if (seq === requestSeq.current) setChecking(false); });
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload は projectId に閉じる
  }, [projectId, digestId, pointIdsKey, durationSec]);

  if (!sourceVideo) return null;

  const toggle = (id: string, on: boolean) => {
    setSourceVideo((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.pointIds);
      if (on) next.add(id); else next.delete(id);
      // digest の順 (重要度順) に揃える
      const ordered = points.map((p) => p.id).filter((pid) => next.has(pid));
      return { ...prev, pointIds: ordered, userTouched: true };
    });
  };
  const resetToSuggested = () => setSourceVideo((prev) => prev && ({ ...prev, pointIds: suggestedPointIds(prev.digest, prev.durationSec), userTouched: false }));
  const clearAll = () => setSourceVideo((prev) => prev && ({ ...prev, pointIds: [], userTouched: true }));

  const sv: SourceVideoState = sourceVideo;
  const meta = [
    '収録動画',
    `${sv.totalSegments}話題`,
    `要点 ${sv.pointIds.length} / ${points.length}件`,
    sv.productId || sv.asin ? '案件を紐づけ済み' : `案件名: ${sv.productName}`,
  ].join('・');
  const omittedNotes = describeOmitted(preview?.omitted);

  return (
    <div className={`${genStyles.productBox} ${cardStyles.card}`} data-testid="source-video-card">
      <span className={cardStyles.cardIcon} aria-hidden="true">
        <img src="/design_file_text_image.svg" alt="" />
      </span>
      <div className={`${genStyles.productBoxInfo} ${styles.body}`}>
        <span className={genStyles.productBoxTitle} title={sv.label}>{sv.label}</span>
        <span className={genStyles.productBoxMeta}>{meta}</span>
        <span className={genStyles.productBoxMeta}>
          AIが整理した要点を重要度順に並べています。動画の目的に合わせて選び直してください。選んだ要点だけを伝え、根拠の話題の本文は文脈として使います
        </span>
        <div className={styles.head}>
          <span>伝える要点</span>
          <span className={styles.headActions}>
            <button type="button" className="btn-link-small" onClick={resetToSuggested} disabled={reloading}>想定尺の既定に戻す</button>
            <button type="button" className="btn-link-small" onClick={clearAll} disabled={reloading}>すべて外す</button>
            {sv.topics.length > 0 && (
              <button type="button" className="btn-link-small" onClick={() => setShowTopics((v) => !v)}>{showTopics ? '元の要点を閉じる' : '元の要点を見る'}</button>
            )}
          </span>
        </div>
        <ul className={styles.points}>
          {points.map((p) => (
            <li key={p.id} className={styles.pointRow}>
              <label className="form-checkbox-label form-checkbox-label--compact">
                <input type="checkbox" checked={selected.has(p.id)} onChange={(e) => toggle(p.id, e.target.checked)} disabled={reloading} />
                <span className={styles.pointRank}>{p.rank}.</span>
                <span>{p.text}</span>
              </label>
              <span className={styles.pointSources}>
                {p.sourceSegmentIds.map((sid) => topicsById.get(sid)).filter((t): t is NonNullable<typeof t> => !!t)
                  .map((t) => `${formatTopicTime(t.startSec)} ${t.title || '(見出しなし)'}`).join(' / ')}
              </span>
            </li>
          ))}
        </ul>
        {showTopics && (
          <ul className={styles.topics} data-testid="source-video-topics">
            {sv.topics.map((t) => (
              <li key={t.id}>
                <span className={styles.topicTime}>{formatTopicTime(t.startSec)}</span>
                <span className={styles.topicKind}>{t.kind === 'demo' ? 'デモ' : '説明'}</span>
                <span>{t.title || '(見出しなし)'}</span>
                {t.keyPoints.length > 0 && <span className={styles.topicPoints}>{t.keyPoints.join(' / ')}</span>}
              </li>
            ))}
          </ul>
        )}
        <div className={styles.preview} role="status" data-testid="source-video-preview">
          {checking || reloading ? (
            <span className={styles.previewChecking}><Loader2 size={13} className={styles.spinner} aria-hidden="true" />{reloading ? '要点を読み直しています' : '確認しています'}</span>
          ) : preview?.ok ? (
            <>
              <span>材料: {preview.chars?.toLocaleString()}字</span>
              {omittedNotes.map((n) => <span key={n} className={styles.previewNote}>{n}</span>)}
            </>
          ) : preview ? (
            <span className={styles.previewError}>{preview.error}</span>
          ) : null}
        </div>
      </div>
      <div className={`${genStyles.productBoxActions} ${cardStyles.cardActions}`}>
        <button
          type="button"
          className={genStyles.productBoxRemove}
          onClick={() => setSourceVideo(null)}
          aria-label="収録動画を外す"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
